import {Injectable, inject} from '@angular/core';
import {AuthenticationService} from './authentication.service';
import {GlobalAlertService} from '../_services/global-alert.service';
import {AlertLevel} from '../enums/alert-level';
import {UserJwt} from '../models/admin_owner/user/user-jwt';

/**
 * How long before {@link UserJwt.contextExpiresAt} the heads-up warning fires. Not scheduled at all when less
 * than this much time remains the moment a session is (re)scheduled — see {@link ContextExpiryTimerService}.
 */
export const CONTEXT_EXPIRY_HEADS_UP_MS = 60_000;

/**
 * Lands an expired time-boxed context session on the chooser PROACTIVELY, on a client timer, instead of only
 * reacting to the 401 the next request happens to make (#279 follow-up hardening).
 *
 * ### Why this exists beside the reactive recovery
 *
 * `ErrorInterceptor` already recovers-or-logs-out on a 401 — see `AuthenticationService.recoverToChooserOrLogout`,
 * which this service calls too. That reactive path is correct but late: it only fires once *something* the user
 * does sends a request against the expired context, so between the actual expiry and that next request the
 * session sits there looking valid while every read is already stale. This service watches
 * `AuthenticationService.user` for {@link UserJwt.contextExpiresAt} and calls the SAME shared method the moment
 * the clock says the context is gone, plus a heads-up toast ~60s ahead of it. The reactive path stays as the
 * guaranteed fallback — a backgrounded tab throttles `setTimeout`, so a proactive timer that fires late (or a
 * tab that was asleep straight through expiry) is still caught by the very next request's 401.
 *
 * ### Why a session change reschedules from scratch
 *
 * `contextExpiresAt` is only ever present on a time-boxed context; an ordinary session, the contextless
 * platform/base session `recoverToChooserOrLogout` itself lands on, and a session with no context at all all
 * omit it — so re-deriving the schedule from the CURRENT session on every emission is what makes a login, a
 * context switch, `recoverToChooserOrLogout`'s own recovery, and a logout all correctly cancel a stale timer
 * (an old company's countdown must never fire against the platform session that replaced it) without a second,
 * separate "clear on logout" code path.
 *
 * ### Lifetime
 *
 * `providedIn: 'root'` — a singleton for the life of the SPA shell. It does nothing until something injects it
 * (Angular DI is lazy), which is why `AppComponent`'s constructor injects it purely for that side effect: this
 * arms the timer both for a session restored from `localStorage` at bootstrap (`AuthenticationService.user` is
 * `BehaviorSubject`-backed, so subscribing here immediately replays the current session) and for one entered
 * later in the running session.
 */
@Injectable({providedIn: 'root'})
export class ContextExpiryTimerService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly alerts = inject(GlobalAlertService);

  /** Pending timers for the current session; cleared wholesale on every reschedule. */
  private timers: ReturnType<typeof setTimeout>[] = [];

  constructor() {
    this.authenticationService.user.subscribe(user => this.reschedule(user));
  }

  /**
   * Re-derives the heads-up + expiry timers from scratch for the given session. Always clears whatever was
   * pending first, so a session with no (or no longer relevant) `contextExpiresAt` is left with nothing armed.
   */
  private reschedule(user: UserJwt | null): void {
    this.clear();

    const expiresAt = user?.contextExpiresAt;
    if (!expiresAt) return;

    const expiryMs = Date.parse(expiresAt);
    if (Number.isNaN(expiryMs)) return;

    const msUntilExpiry = expiryMs - Date.now();
    if (msUntilExpiry <= 0) {
      // Already past by the time this session was (re)scheduled — e.g. a tab restored from a background/sleep
      // spell after the instant had already gone by. Run the same recovery-or-logout immediately rather than
      // leaving the (already-expired) session to sit there until the next request 401s.
      this.expire();
      return;
    }

    // Only worth a heads-up if there was ever ≥ the lead time left at scheduling time — a session rescheduled
    // with less than that remaining (e.g. the tab woke up 20s before expiry) goes straight to the silent
    // countdown; a "your access expires in ~1 minute" toast when it is actually expiring in ten seconds would
    // just be wrong.
    if (msUntilExpiry > CONTEXT_EXPIRY_HEADS_UP_MS) {
      this.timers.push(setTimeout(() => this.headsUp(), msUntilExpiry - CONTEXT_EXPIRY_HEADS_UP_MS));
    }
    this.timers.push(setTimeout(() => this.expire(), msUntilExpiry));
  }

  private headsUp(): void {
    this.alerts.createAndShow(
      'Zugang läuft bald ab',
      'Ihr temporärer Zugang läuft in etwa 1 Minute ab.',
      AlertLevel.warning,
      8000
    );
  }

  /**
   * The context has (or is deemed to have) expired: hand off to the SAME recover-or-logout
   * `ErrorInterceptor` uses reactively, so a proactive fire and a racing 401 can never mint two recoveries or
   * disagree on the outcome — see `AuthenticationService.recoverToChooserOrLogout`'s shared in-flight guard.
   */
  private expire(): void {
    this.clear();
    this.authenticationService.recoverToChooserOrLogout();
  }

  private clear(): void {
    for (const handle of this.timers) clearTimeout(handle);
    this.timers = [];
  }
}
